import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * Shared by DialogContent and AlertDialogContent:
 *  - never taller than the window: a long dialog scrolls instead of pushing
 *    its title and buttons off-screen (2026-10 payroll lock dialog);
 *  - 16px padding on phones, 24px from `sm` (`--dialog-pad`, which the
 *    sticky footer bleeds back over);
 *  - `[&>*]:min-w-0` + a minmax(0) column: one long word or no-wrap button
 *    can't widen the dialog past a 320px screen;
 *  - `wr-dialog` marks the scroll box for the sticky footer.
 */
export const dialogContentClass =
  "wr-dialog fixed inset-0 z-50 m-auto grid h-fit max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg grid-cols-[minmax(0,1fr)] gap-4 overflow-y-auto rounded-lg border bg-background p-[var(--dialog-pad)] shadow-lg outline-none duration-200 [--dialog-pad:1rem] sm:[--dialog-pad:1.5rem] [&>*]:min-w-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"

/**
 * Shared by DialogFooter and AlertDialogFooter (docs/71 §9):
 *  - phones: buttons stack full width with a gap, the primary on top, and a
 *    long label wraps instead of widening the dialog;
 *  - inside a dialog it is sticky to the bottom of the scroll box, so in a
 *    long form the primary action never sits below the fold. It bleeds over
 *    the dialog's padding so content scrolling under it is hidden, with a
 *    short fade above it. Where nothing scrolls it looks exactly as before.
 *    `data-sticky="false"` opts out.
 */
export const dialogFooterClass =
  "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end max-sm:[&>:is(button,a)]:h-auto max-sm:[&>:is(button,a)]:min-h-11 max-sm:[&>:is(button,a)]:whitespace-normal max-sm:[&>:is(button,a)]:py-2 max-sm:[&>:is(button,a)]:text-center in-[.wr-dialog]:not-data-[sticky=false]:sticky in-[.wr-dialog]:not-data-[sticky=false]:-bottom-[var(--dialog-pad)] in-[.wr-dialog]:not-data-[sticky=false]:z-10 in-[.wr-dialog]:not-data-[sticky=false]:-mx-[var(--dialog-pad)] in-[.wr-dialog]:not-data-[sticky=false]:-mb-[var(--dialog-pad)] in-[.wr-dialog]:not-data-[sticky=false]:bg-background in-[.wr-dialog]:not-data-[sticky=false]:px-[var(--dialog-pad)] in-[.wr-dialog]:not-data-[sticky=false]:pb-[var(--dialog-pad)] in-[.wr-dialog]:not-data-[sticky=false]:before:pointer-events-none in-[.wr-dialog]:not-data-[sticky=false]:before:absolute in-[.wr-dialog]:not-data-[sticky=false]:before:inset-x-0 in-[.wr-dialog]:not-data-[sticky=false]:before:-top-3 in-[.wr-dialog]:not-data-[sticky=false]:before:h-3 in-[.wr-dialog]:not-data-[sticky=false]:before:bg-gradient-to-t in-[.wr-dialog]:not-data-[sticky=false]:before:from-background in-[.wr-dialog]:not-data-[sticky=false]:before:to-transparent"

/**
 * Touch screens (coarse pointer): opening a dialog must not focus its first
 * field — that raises the on-screen keyboard over half the dialog before
 * the person has read it. The dialog itself takes focus instead (screen
 * readers still land inside it). A caller's own `onOpenAutoFocus` runs
 * first and wins when it calls `preventDefault()`.
 */
function isCoarsePointer(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches
  )
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, onOpenAutoFocus, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      onOpenAutoFocus={(e) => {
        onOpenAutoFocus?.(e)
        if (e.defaultPrevented || !isCoarsePointer()) return
        e.preventDefault()
        if (e.target instanceof HTMLElement) e.target.focus({ preventScroll: true })
      }}
      className={cn(dialogContentClass, className)}
      {...props}
    >
      {children}
      {/* 16px glyph in a real 44px target (docs/71 §63); offset so the glyph stays 24px from the corner. */}
      <DialogPrimitive.Close className="absolute right-0.5 top-0.5 inline-flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground ring-offset-background transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn(dialogFooterClass, className)} {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      // Clear of the close button in the top-right corner.
      "pr-8 text-lg font-semibold leading-none tracking-tight sm:pr-4",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
